import { readSettings } from "./control.js";
import { defaultOptions, optionFields, type ToolOptions } from "./options.js";

/**
 * Remembering the settings between visits.
 *
 * A teacher who wants tone numbers and no word spacing wants that every time,
 * and setting it again on each visit is the kind of small friction that stops
 * a tool being used. The pinyin settings are shared across the tools that have
 * them for the same reason: they are one preference about how pinyin should be
 * written, not several.
 *
 * Nothing here is required for the page to work. Storage can be unavailable
 * (Safari's private browsing has historically thrown on write, and a visitor
 * can block it outright), so every path falls back to the defaults rather than
 * failing.
 */

/** Where the pinyin settings are kept. Namespaced, since the site shares an origin. */
const optionsKey = "chineseboost.tools.options";

/**
 * Settings from a previous visit, or the defaults.
 *
 * What comes back is checked field by field against what each offers, because
 * `localStorage` is untrusted input like any other: it may have been written
 * by a version of this tool that offered choices this one does not, or edited
 * by hand.
 */
export function loadSettings<Settings extends object>(
  storage: Storage | undefined,
  key: string,
  read: (value: unknown) => Settings,
  defaults: Settings,
): Settings {
  if (storage === undefined) {
    return defaults;
  }

  try {
    const stored = storage.getItem(key);

    return stored === null ? defaults : read(JSON.parse(stored));
  } catch {
    return defaults;
  }
}

/**
 * Remember a set of settings, if the browser will let us.
 */
export function saveSettings(
  storage: Storage | undefined,
  key: string,
  settings: object,
): void {
  try {
    storage?.setItem(key, JSON.stringify(settings));
  } catch {
    // A visitor who has blocked storage still gets a working tool; they just
    // get the defaults again next time.
  }
}

/** The pinyin settings from a previous visit, or the defaults. */
export function loadOptions(storage: Storage | undefined): ToolOptions {
  return loadSettings(
    storage,
    optionsKey,
    (value) => readSettings(optionFields, defaultOptions, value),
    defaultOptions,
  );
}

/** Remember the pinyin settings, if the browser will let us. */
export function saveOptions(
  storage: Storage | undefined,
  options: ToolOptions,
): void {
  saveSettings(storage, optionsKey, options);
}

/**
 * The browser's local storage, where there is one.
 *
 * Reading the property can itself throw when storage is disabled, which is why
 * this is a function with a `try` rather than a reference to `localStorage`.
 */
export function browserStorage(): Storage | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}
